
import { useTranslation } from 'react-i18next';

interface HeroSectionProps {
  onContact: () => void;
}

/**
 * HeroSection – displays a full‑screen background video with
 * a title, subtitle, call‑to‑action button and a small badge.
 *
 * The component is deliberately kept simple and defensive:
 * - The `onContact` callback is wrapped in a try/catch to avoid
 *   uncaught errors breaking the UI.
 * - Translation keys are accessed safely; if a key is missing,
 *   a fallback string is shown instead of throwing.
 */
export default function HeroSection({ onContact }: HeroSectionProps) {
  const { t } = useTranslation();

  // Helper to safely retrieve translation strings
  const safeT = (key: string, fallback: string) => {
    try {
      const translated = t(key);
      // i18next returns the key itself when a translation is missing
      return translated !== key ? translated : fallback;
    } catch {
      return fallback;
    }
  };

  const handleContact = () => {
    try {
      onContact?.();
    } catch (err) {
      // Log the error for debugging but keep the UI stable
      console.error('HeroSection: onContact handler threw an error', err);
    }
  };

  return (
    <section className="relative min-h-[100svh] w-full overflow-hidden">
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
      >
        <source
          src="https://cdn.unionlab.ai/PPPHEROVIDEO.mp4"
          type="video/mp4"
        />
      </video>

      <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-black/10 to-black/20"></div>

      <div className="relative z-10 min-h-[100svh] flex items-end justify-center pb-10 sm:pb-32 pt-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 text-center w-full">
          <h1 className="text-[1.6rem] leading-[1.2] sm:text-5xl md:text-6xl font-bold text-white mb-3 sm:mb-6">
            {safeT('hero.title', 'Welcome')}
          </h1>
          <p className="text-[0.9rem] leading-relaxed sm:text-xl md:text-2xl text-white/90 mb-5 sm:mb-10 font-light max-w-2xl mx-auto">
            {safeT('hero.subtitle', 'Your subtitle goes here.')}
          </p>

          <button
            onClick={handleContact}
            className="inline-flex items-center bg-[#d6df00] text-[#1A1A1A] px-5 sm:px-8 py-2.5 sm:py-4 rounded-full text-[0.9rem] sm:text-lg font-medium hover:bg-[#c2cb00] transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
          >
            {safeT('hero.cta', 'Contact Us')}
            <i className="ri-arrow-right-line ml-2 text-lg sm:text-xl"></i>
          </button>

          <div className="mt-5 sm:mt-10 flex items-center justify-center space-x-3 text-white/80 text-xs sm:text-sm">
            <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
              <i className="ri-global-line text-lg sm:text-xl"></i>
            </div>
            <span>{safeT('hero.badge', 'Global')}</span>
          </div>
        </div>
      </div>
    </section>
  );
}
